import { computed, inject, Injectable, resource } from '@angular/core';
import { keepLastResolvedValue } from '@okcargo-apps/layout-ionic';
import { IDecaDocumentCompany } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { ConnectivityService, DecaQueryService, PartialSearchOptions } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account';

@Injectable({
  providedIn: 'root',
})
export class DecaCompanyStoreService {
  private readonly decaQueryService = inject(DecaQueryService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly PAGE_INDEX = 0;
  private readonly PAGE_SIZE = 10000;

  private readonly _companies = resource({
    params: () => {
      const canView =
        this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
        this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER);

      return canView ? { isValid: true } : null;
    },
    loader: async ({ params, abortSignal }): Promise<IDecaDocumentCompany[]> => {
      if (!params) return [];

      const options: PartialSearchOptions = {
        criteriaPayload: {
          pageIndex: this.PAGE_INDEX,
          pageSize: this.PAGE_SIZE,
          order: [],
        },
        abortSignal,
      };

      return await this.decaQueryService.searchDecaCompanies(options);
    },
  });

  /*
   * The companies are read through the last resolved value, so a reload that fails (e.g. the
   * connection drops in the middle of a refresh) keeps the list instead of breaking the view:
   * reading the `value` of an errored resource throws.
   */
  private readonly _lastResolvedCompanies = keepLastResolvedValue(this._companies.snapshot);

  public readonly companies = computed(() => this._lastResolvedCompanies() ?? []);

  public readonly companiesLoading = this._companies.isLoading;

  public reloadCompanies(): void {
    if (this.connectivity.isOffline()) return;
    this._companies.reload();
  }

  public reloadAfterSaveData(): void {
    setTimeout(() => {
      this.reloadCompanies();
    }, 2000); // 2 sec
  }
}
